import { useState, type FormEvent } from "react";
import ErrorBanner from "./ErrorBanner";
import PrimaryButton from "./PrimaryButton";
import { extractApiErrorMessage } from "../api/client";

interface AuthFormPanelProps {
    mode: "login" | "register";
    onClose: () => void;
    onSubmit: (username: string, password: string, rememberMe: boolean) => Promise<void>;
}

const COPY = {
    login: {
        title: "Accedi",
        submitLabel: "Accedi",
        fallbackError: "Accesso non riuscito. Riprova.",
    },
    register: {
        title: "Registrati",
        submitLabel: "Crea account",
        fallbackError: "Registrazione non riuscita. Riprova.",
    },
} as const;

export default function AuthFormPanel({ mode, onClose, onSubmit }: AuthFormPanelProps) {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [rememberMe, setRememberMe] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const copy = COPY[mode];

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            await onSubmit(username, password, rememberMe);
            onClose();
        } catch (err: unknown) {
            setError(extractApiErrorMessage(err, copy.fallbackError));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            {/* Backdrop: click anywhere outside the panel to close. */}
            <div className="fixed inset-0 z-40" onClick={onClose} aria-hidden="true" />
            <div
                role="dialog"
                aria-label={copy.title}
                className="absolute right-0 top-full z-50 mt-2 w-72 rounded-lg border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-800"
            >
                <h2 className="mb-3 text-sm font-bold text-gray-900 dark:text-gray-100">
                    {copy.title}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-3">
                    <div>
                        <label
                            htmlFor={`${mode}-username`}
                            className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300"
                        >
                            Nome utente
                        </label>
                        <input
                            id={`${mode}-username`}
                            type="text"
                            required
                            autoComplete="username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                        />
                    </div>
                    <div>
                        <label
                            htmlFor={`${mode}-password`}
                            className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300"
                        >
                            Password
                        </label>
                        <input
                            id={`${mode}-password`}
                            type="password"
                            required
                            autoComplete={mode === "login" ? "current-password" : "new-password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                        />
                    </div>
                    {mode === "login" && (
                        <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                            <input
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                className="h-4 w-4 rounded border-gray-300"
                            />
                            Ricordami
                        </label>
                    )}
                    {error && <ErrorBanner message={error} />}
                    <PrimaryButton type="submit" disabled={submitting} className="w-full">
                        {submitting ? "Attendere…" : copy.submitLabel}
                    </PrimaryButton>
                </form>
            </div>
        </>
    );
}
